import { useState, useEffect, useCallback } from 'react'
import { appsyncQuery } from '../api/appsync-client.js'

/**
 * useRating — fetch and submit the session rating pair.
 *
 * Returns { ratingPair, loading, error, submitRating, reload }.
 *
 * Redaction contract: partner.score / partner.note are only present when
 * partner.revealedAt is non-null (server-authoritative). The hook does NOT
 * add or synthesize these fields — it passes the server payload through as-is.
 *
 * Live-reveal push intentionally deferred (SEC-fu-F1, #534). The hook
 * re-fetches after submitRating returns so the caller sees the reveal if
 * both parties have now submitted.
 * // Phase G follow-up: wire onRatingRevealed subscription once SEC-fu-F1 lands.
 *
 * Phase G: sessionId required. Enabled flag mirrors useInSession pattern.
 */

const GET_RATING_PAIR = `
  query GetRatingPair($sessionId: ID!) {
    getRatingPair(sessionId: $sessionId) {
      me {
        submitted ratingId sessionId authorId score note submittedAt revealedAt
      }
      partner {
        submitted score note revealedAt
      }
    }
  }
`

const SUBMIT_RATING = `
  mutation SubmitRating($sessionId: ID!, $input: SubmitRatingInput!) {
    submitRating(sessionId: $sessionId, input: $input) {
      me {
        submitted ratingId sessionId authorId score note submittedAt revealedAt
      }
      partner {
        submitted score note revealedAt
      }
    }
  }
`

export function useRating(sessionId, { enabled = true } = {}) {
  const [ratingPair, setRatingPair] = useState(null)
  const [loading, setLoading] = useState(!!(sessionId && enabled))
  const [error, setError] = useState(null)

  const load = useCallback(async () => {
    if (!sessionId) return
    setLoading(true)
    setError(null)
    try {
      const data = await appsyncQuery(GET_RATING_PAIR, { sessionId })
      setRatingPair(data.getRatingPair)
    } catch (err) {
      setError(err)
    } finally {
      setLoading(false)
    }
  }, [sessionId])

  useEffect(() => {
    if (enabled && sessionId) load()
  }, [load, enabled, sessionId])

  const submitRating = useCallback(
    async (score, note) => {
      const input = { score, ...(note != null && note !== '' && { note }) }
      const data = await appsyncQuery(SUBMIT_RATING, { sessionId, input })
      const pair = data.submitRating
      setRatingPair(pair)
      return pair
    },
    [sessionId],
  )

  return {
    ratingPair,
    loading,
    error,
    reload: load,
    submitRating,
  }
}
